import { useState, useMemo, useId } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Search, X } from 'lucide-react';
import { Container } from '@/components/layout/Container';
import { Section } from '@/components/layout/Section';
import { Heading } from '@/components/layout/Heading';
import { RouteHead } from '@/components/seo/RouteHead';
import { StructuredData } from '@/components/seo/StructuredData';
import { ROUTES } from '@/routes';
import { fadeUp, fadeIn, staggerContainer, VIEWPORT_REVEAL, STAGGER } from '@/lib/motion';

interface FaqItem {
  id: number;
  question: string;
  answer: string;
}

const FAQ_IDS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12] as const;
type FaqId = (typeof FAQ_IDS)[number];

function FaqAccordionItem({ item, isOpen, onToggle }: {
  item: FaqItem;
  isOpen: boolean;
  onToggle: () => void;
}) {
  const answerId = useId();

  return (
    <div className="border-b border-border last:border-0">
      <button
        type="button"
        aria-expanded={isOpen}
        aria-controls={answerId}
        onClick={onToggle}
        className="flex w-full items-start justify-between gap-4 py-5 text-start text-sm font-semibold transition-colors duration-fast ease-out-quart hover:text-brand focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
      >
        <span>{item.question}</span>
        <ChevronDown
          aria-hidden="true"
          className={`mt-0.5 h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-fast ease-out-quart ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            id={answerId}
            role="region"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.18, ease: [0.25, 1, 0.5, 1] }}
            className="overflow-hidden"
          >
            <p className="pb-5 text-sm leading-relaxed text-muted-foreground">{item.answer}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function FaqPage() {
  const { t } = useTranslation('faq');
  const [query, setQuery] = useState('');
  const [openIds, setOpenIds] = useState<Set<number>>(new Set());

  const allItems: FaqItem[] = useMemo(
    () =>
      FAQ_IDS.map((n: FaqId) => ({
        id: n,
        question: t(`item_${n}_q`),
        answer: t(`item_${n}_a`),
      })),
    [t],
  );

  const faqSchema: Record<string, unknown> = useMemo(
    () => ({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: allItems.map((item) => ({
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: { '@type': 'Answer', text: item.answer },
      })),
    }),
    [allItems],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return allItems;
    return allItems.filter(
      (item) =>
        item.question.toLowerCase().includes(q) ||
        item.answer.toLowerCase().includes(q),
    );
  }, [allItems, query]);

  function toggle(id: number) {
    setOpenIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }

  return (
    <>
      <RouteHead
        path={ROUTES.faq}
        titleKey="seo:faq.title"
        descriptionKey="seo:faq.description"
      />
      <StructuredData schema={faqSchema} />
      {/* Hero */}
      <Section padding="lg">
        <Container size="md" className="text-center">
          <motion.div
            variants={staggerContainer(STAGGER.base)}
            initial="hidden"
            animate="visible"
          >
            <motion.p
              variants={fadeIn}
              className="mb-4 text-xs font-semibold uppercase tracking-widest text-brand"
            >
              {t('page.title')}
            </motion.p>
            <motion.div variants={fadeUp}>
              <Heading level={1}>{t('page.headline')}</Heading>
            </motion.div>
            <motion.p
              variants={fadeUp}
              className="mx-auto mt-6 max-w-2xl text-lg text-muted-foreground"
            >
              {t('page.subheadline')}
            </motion.p>

            {/* Search */}
            <motion.div variants={fadeUp} className="mx-auto mt-10 max-w-lg">
              <div className="relative">
                <Search
                  aria-hidden="true"
                  className="pointer-events-none absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                />
                <input
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={t('search.placeholder')}
                  aria-label={t('search.aria_label')}
                  className="w-full rounded-xl border border-border bg-card py-3 ps-10 pe-10 text-sm placeholder:text-muted-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
                />
                {query && (
                  <button
                    type="button"
                    onClick={() => setQuery('')}
                    aria-label={t('search.clear_aria_label')}
                    className="absolute end-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
                  >
                    <X className="h-4 w-4" aria-hidden="true" />
                  </button>
                )}
              </div>
            </motion.div>
          </motion.div>
        </Container>
      </Section>

      {/* Accordion list */}
      <Section padding="lg" bordered>
        <Container size="md">
          {filtered.length === 0 ? (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              className="py-16 text-center"
            >
              <p className="font-semibold">
                {t('search.no_results_headline')}{' '}
                <span className="text-brand">&ldquo;{query}&rdquo;</span>
              </p>
              <p className="mt-2 text-sm text-muted-foreground">{t('search.no_results_body')}</p>
            </motion.div>
          ) : (
            <motion.div
              variants={staggerContainer(STAGGER.base)}
              initial="hidden"
              whileInView="visible"
              viewport={VIEWPORT_REVEAL}
              className="rounded-2xl border border-border bg-card px-6 shadow-soft"
            >
              <AnimatePresence mode="popLayout">
                {filtered.map((item) => (
                  <motion.div
                    key={item.id}
                    variants={fadeUp}
                    layout
                  >
                    <FaqAccordionItem
                      item={item}
                      isOpen={openIds.has(item.id)}
                      onToggle={() => toggle(item.id)}
                    />
                  </motion.div>
                ))}
              </AnimatePresence>
            </motion.div>
          )}
        </Container>
      </Section>

      {/* CTA */}
      <Section padding="lg" className="bg-primary-soft">
        <Container size="sm" className="text-center">
          <motion.div
            variants={staggerContainer(STAGGER.base)}
            initial="hidden"
            whileInView="visible"
            viewport={VIEWPORT_REVEAL}
          >
            <motion.div variants={fadeUp}>
              <Heading level={2}>{t('cta.headline')}</Heading>
            </motion.div>
            <motion.p variants={fadeUp} className="mx-auto mt-4 max-w-md text-muted-foreground">
              {t('cta.body')}
            </motion.p>
            <motion.div variants={fadeUp} className="mt-6">
              <a
                href="mailto:hello@meetblast.net"
                className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-6 py-3 text-sm font-semibold transition-colors duration-fast ease-out-quart hover:border-brand/50 hover:text-brand"
              >
                {t('cta.link')}
              </a>
            </motion.div>
          </motion.div>
        </Container>
      </Section>
    </>
  );
}
